import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ArrowLeft, ClipboardCheck, MapPin, Search, Download, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { apiGet, apiPost } from '../../lib/api';
import SVRForm from '../../components/SVRForm';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Mobile Site Visit Report page.
 *
 * Two views:
 *  1. Picker — choose an existing enquiry from a searchable list.
 *  2. Form   — renders <SVRForm> in compact mode for the chosen enquiry.
 *
 * Enquiries (with their client + project, migrated from the BD template) are
 * created in the Enquiry Tracker. This screen NEVER creates a new enquiry or a
 * project — a Site Visit Report is always attached to an enquiry the user picks
 * below, so there is no risk of duplicate projects / clients from here.
 */
export default function MobileSiteVisit() {
  const [params, setParams] = useSearchParams();
  const enquiryId = params.get('enquiry_id');
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [hasSvr, setHasSvr] = useState(false);  // true once SVR exists or has been saved

  useEffect(() => {
    if (!enquiryId) {
      setLoading(true);
      apiGet('/enquiries')
        .then((r) => setEnquiries(Array.isArray(r.items) ? r.items : []))
        .catch(() => toast.error('Could not load enquiries'))
        .finally(() => setLoading(false));
    }
  }, [enquiryId]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return enquiries;
    return enquiries.filter((e) =>
      [e.project_name, e.client, e.location, e.enquiry_no, e.stage]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [enquiries, query]);

  // ── Form view ──────────────────────────────────────────────────────────
  if (enquiryId) {
    const downloadSvrPdf = async () => {
      try {
        const token = localStorage.getItem('access_token');
        const res = await fetch(`${API}/api/enquiries/${enquiryId}/site-visit-report/pdf`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) throw new Error('PDF generation failed');
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url;
        a.download = `SVR_${enquiryId}.pdf`;
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch { toast.error('Failed to download PDF'); }
    };

    return (
      <div className="bg-[#FDFDFD] min-h-screen">
        <header className="sticky top-0 z-20 bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
          <button onClick={() => setParams({})} className="flex items-center gap-1 text-xs font-display font-bold uppercase tracking-wider text-slate-600" data-testid="m-svr-back">
            <ArrowLeft size={14} /> Back
          </button>
          <h1 className="font-display font-bold text-sm uppercase tracking-wide flex items-center gap-1.5">
            <ClipboardCheck size={15} /> Site Visit Report
          </h1>
          {hasSvr && (
            <button onClick={downloadSvrPdf}
              className="flex items-center gap-1 text-xs font-display font-bold text-[#3b4872] uppercase tracking-wider"
              data-testid="m-svr-download-pdf-btn">
              <Download size={14} /> PDF
            </button>
          )}
          {!hasSvr && <div className="w-12" />}
        </header>
        <div className="p-3">
          <SVRForm enquiryId={enquiryId} onSaved={() => { toast.success('Saved'); setParams({}); }} onHasSvr={setHasSvr} compact />
          {/* Batch C follow-up: mobile GO/NO-GO decision buttons after SVR is saved */}
          {hasSvr && <MobileSVRDecision enquiryId={enquiryId} onDone={() => setParams({})} />}
        </div>
      </div>
    );
  }

  // ── Picker view ────────────────────────────────────────────────────────
  return (
    <div className="bg-[#FDFDFD] min-h-screen">
      <header className="sticky top-0 z-20 bg-white border-b border-slate-200 px-4 py-3">
        <h1 className="font-display font-bold text-sm uppercase tracking-wide flex items-center gap-1.5">
          <ClipboardCheck size={15} /> Site Visit Report
        </h1>
        <p className="text-[11px] text-slate-500 mt-0.5">Select an enquiry to start its Site Visit Report</p>
      </header>

      <div className="p-3 space-y-3">
        {/* Search */}
        <div className="relative" data-testid="m-svr-search-wrap">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by project, client or location…"
            className="w-full border border-slate-300 rounded-sm pl-9 pr-3 py-2.5 text-sm"
            data-testid="m-svr-search"
          />
        </div>

        <div>
          <div className="text-[10px] font-display font-bold uppercase tracking-wider text-slate-500 mb-2" data-testid="m-svr-list-label">
            {query ? `Results · ${filtered.length}` : 'Enquiries'}
          </div>

          {loading ? (
            <div className="text-xs text-slate-400 text-center py-8 bg-white border border-slate-200 rounded-sm" data-testid="m-svr-loading">Loading enquiries…</div>
          ) : filtered.length === 0 ? (
            <div className="text-xs text-slate-400 text-center py-8 bg-white border border-slate-200 rounded-sm" data-testid="m-svr-empty">
              {enquiries.length === 0 ? 'No enquiries yet. Create one from the Enquiry Tracker first.' : 'No enquiries match your search.'}
            </div>
          ) : (
            <div className="space-y-2">
              {filtered.map((e) => (
                <button key={e.id} onClick={() => setParams({ enquiry_id: e.id })}
                  className="w-full text-left bg-white border border-slate-200 rounded-sm p-3 hover:border-[#3b4872] active:border-[#3b4872]"
                  data-testid={`m-svr-enq-${e.id}`}>
                  <div className="font-display font-bold text-xs">{e.project_name || '—'}</div>
                  <div className="text-[11px] text-slate-600">{e.client}</div>
                  <div className="flex items-center gap-2 mt-1 text-[10px] text-slate-500">
                    {e.location && <span className="flex items-center gap-0.5"><MapPin size={9} /> {e.location}</span>}
                    {e.stage && <span className="bg-slate-100 px-1.5 py-0.5 rounded-sm">{e.stage}</span>}
                    {e.site_visit_report && <span className="text-emerald-600 font-bold">SVR ✓</span>}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// Batch C follow-up: Mobile GO/NO-GO decision after SVR is filed
function MobileSVRDecision({ enquiryId }) {
  const [enq, setEnq] = React.useState(null);
  const [note, setNote] = React.useState('');
  const [submitting, setSubmitting] = React.useState(null); // 'GO'|'NO-GO'|null

  React.useEffect(() => {
    apiGet(`/enquiries/${enquiryId}`)
      .then(d => setEnq(d?.enquiry || d))
      .catch(() => {});
  }, [enquiryId]);

  if (!enq) return null;
  const decision = enq.final_decision || enq.provisional_decision;
  if (decision) {
    return (
      <div className="mt-4 bg-slate-50 border border-slate-200 rounded-sm p-4 text-sm text-center text-slate-500" data-testid="m-svr-decision-recorded">
        Decision recorded: <strong>{decision}</strong>
      </div>
    );
  }

  const record = async (d) => {
    setSubmitting(d);
    try {
      const isProvisional = enq.stage === 'Provisional Qualification';
      const endpoint = isProvisional
        ? `/enquiries/${enquiryId}/provisional-decision`
        : `/enquiries/${enquiryId}/final-decision`;
      await apiPost(endpoint, { decision: d, note: note.trim() });
      toast.success(`${d} decision recorded`);
      // Tendering_30Sep2026 §8: reflect the decision in this panel right away
      // instead of relying on the next click's re-fetch to reveal it.
      setEnq((prev) => ({ ...prev, [isProvisional ? 'provisional_decision' : 'final_decision']: d }));
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setSubmitting(null); }
  };

  return (
    <div className="mt-4 border border-slate-200 rounded-sm p-4 space-y-3 bg-white" data-testid="m-svr-decision-panel">
      <p className="text-xs font-semibold text-slate-700">Record GO / NO-GO decision</p>
      <textarea value={note} onChange={e => setNote(e.target.value)}
        placeholder="Optional note for the decision record" rows={2}
        className="w-full border border-slate-200 rounded-sm px-2 py-1.5 text-xs" />
      <div className="grid grid-cols-2 gap-3">
        <button onClick={() => record('GO')} disabled={!!submitting} data-testid="m-svr-go-btn"
          className="flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white py-2.5 rounded-sm text-sm font-semibold disabled:opacity-50">
          <CheckCircle size={16} /> {submitting === 'GO' ? '…' : 'GO'}
        </button>
        <button onClick={() => record('NO-GO')} disabled={!!submitting} data-testid="m-svr-nogo-btn"
          className="flex items-center justify-center gap-2 bg-rose-600 hover:bg-rose-700 text-white py-2.5 rounded-sm text-sm font-semibold disabled:opacity-50">
          <XCircle size={16} /> {submitting === 'NO-GO' ? '…' : 'NO-GO'}
        </button>
      </div>
      <p className="text-[10px] text-slate-400">NO-GO parks the enquiry; re-entry is allowed.</p>
    </div>
  );
}
